import { init, Types, setTransport, flush, getSessionId, getDeviceId, getUserId, Identify, identify, track } from '@amplitude/analytics-browser';
import { deleteVendorConfigCookie, setVendorConfigCookie } from '../utils/config.mjs';
import { devLogger } from '../utils/devLogger.mjs';
import { getEventKey } from '../utils/getEventKey.mjs';
import { createQueue } from '../utils/simpleQueue.mjs';
import { UUID } from '../utils/uuid.mjs';

const GET_CONFIG_TIMEOUT = 1e4;
const blockedEventQueue = createQueue();
const blockedUserPropertiesQueue = createQueue();
const configWaitingList = createQueue();
let isInitialized = false;
let useDryRun;
const amplitudeLogger = {
  config: ({ dryRun }) => {
    if (typeof dryRun === "boolean") {
      useDryRun = dryRun;
    }
  },
  initialize: (apiKey, identifier, { serverUrl, keepAlive, deviceId, onError } = {}) => {
    const { userIdHash, ...restIdentifier } = identifier ?? {};
    if (isInitialized) {
      return;
    }
    if (useDryRun) {
      isInitialized = true;
      flushPendingEvents();
      return;
    }
    const keepAliveConfigs = {
      transport: Types.TransportType.SendBeacon,
      flushIntervalMillis: 1,
      flushQueueSize: 10
    };
    init(apiKey, userIdHash, {
      deviceId,
      serverUrl,
      ...keepAlive ? keepAliveConfigs : {}
    }).promise.then(() => {
      isInitialized = true;
      flushPendingEvents();
      if (typeof document !== "undefined") {
        deleteVendorConfigCookie();
        setVendorConfigCookie(getAmplitudeConfig());
      }
      amplitudeLogger.setUserProperties(restIdentifier);
    }).catch((error) => {
      onError?.(error);
    });
    if (keepAlive) {
      window.addEventListener("pagehide", () => {
        setTransport(Types.TransportType.SendBeacon);
        flush();
      });
    }
  },
  sendEvent: (params) => {
    if (!isInitialized) {
      blockedEventQueue.enqueue(params);
      return;
    }
    const { eventProperties, ...rest } = params;
    const eventKey = getEventKey(rest);
    if (useDryRun) {
      devLogger.log("track ", params);
    } else {
      track(eventKey, eventProperties);
    }
  },
  setUserProperties: (userProperties) => {
    if (!isInitialized) {
      blockedUserPropertiesQueue.enqueue(userProperties);
      return;
    }
    for (const [key, value] of Object.entries(userProperties)) {
      if (useDryRun) {
        devLogger.log("identify ", key, value);
      } else {
        const identity = new Identify().set(key, value);
        identify(identity);
      }
    }
  },
  getConfig: () => {
    if (isInitialized) {
      return new Promise((res) => {
        res(getAmplitudeConfig());
      });
    }
    return new Promise((res) => {
      configWaitingList.enqueue(res);
      window.setTimeout(() => {
        if (configWaitingList.pick(res) !== void 0) {
          configWaitingList.omit(res);
          res(getAmplitudeConfig());
        }
      }, GET_CONFIG_TIMEOUT);
    });
  }
};
const getAmplitudeConfig = () => {
  if (useDryRun) {
    return {
      userId: UUID(),
      // https://amplitude.com/glossary/terms/user-id
      deviceId: UUID(),
      // https://github.com/amplitude/Amplitude-TypeScript/blob/main/packages/analytics-core/src/utils/uuid.ts#L9
      sessionId: Date.now()
      // https://github.com/amplitude/Amplitude-TypeScript/blob/main/packages/analytics-browser/src/browser-client.ts#L77
    };
  }
  try {
    return {
      userId: getUserId(),
      deviceId: getDeviceId(),
      sessionId: getSessionId()
    };
  } catch {
    return {
      userId: void 0,
      deviceId: void 0,
      sessionId: void 0
    };
  }
};
const flushPendingEvents = () => {
  blockedEventQueue.flush(amplitudeLogger.sendEvent);
  blockedUserPropertiesQueue.flush(amplitudeLogger.setUserProperties);
  const amplitudeConfig = getAmplitudeConfig();
  configWaitingList.flush((res) => res(amplitudeConfig));
};

export { amplitudeLogger };
//# sourceMappingURL=amplitude.mjs.map
